import { ReloadOutlined } from "@ant-design/icons"
import { CurrencyEnum, PerDiem, PerDiemDailyExpenses, PriceIntervalEnum } from "@finway-group/shared/lib/models"
import { Button, Col, DatePicker, Divider, Form, Input, Row, Tabs } from "antd"
import { NamePath } from "antd/lib/form/interface"
import { ExpenseRules } from "Components/forms/rules"
import { ExpenditureRules } from "Components/forms/rules/perDiem.rules"
import { useExpenseFormContext } from "Components/modals/expenseCreateForm.context"
import PriceLabel from "Components/priceLabel"
import { isPerDiemExpense } from "Features/pages/expenses/expenseUtils"
import moment, { Moment } from "moment"
import React, { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { usePerDiemDestinationMap } from "Shared/hooks/perDiemDestination.hooks"
import { getPopupAnchor } from "Shared/utils"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import useUpdateEffect from "Shared/utils/hooks/useUpdateEffect"
import ExpenseCommonInputFieldsForm from "../../commonInputFields/commonInputFields.form"
import ExpenseFormLayout from "../../expenseFormLayout"
import { HoverableInfo } from "../auxilliary/HoverableInfo"
import ReimbursementTypeSelector from "../reimbursementTypeSelector"
import { calculateTotalReimbursement, formatPerDiemDestinationDates } from "./helpers"
import { PerDiemDestinationFormFieldsContainer } from "./perDiemDestinationFormFields.container"
import { PerDiemOptionTable } from "./perDiemOptionTable"
import { PerDiemReimbursementsPerDayTable } from "./perDiemReimbursementsPerDayTable"

const PER_DIEM_DURATION_LIMIT = 90 // 90 days
export interface FormPerDiemDestinationItinerary {
    startDate?: Moment
    endDate?: Moment
    destination?: string
}

export interface PerDiemDestinationItinerary {
    startDate: Date
    endDate: Date
    destination: string
}

type PerDiemDailyExpensesWithMomentDate = Omit<PerDiemDailyExpenses, "date"> & { date: Moment }

interface PerDiemRequestFormInterface {}
export const PerDiemRequestForm: React.FC<PerDiemRequestFormInterface> = ({}) => {
    const { t } = useTranslation()
    const employees = useEmployees()

    const [{ expenseForm, updateExpense, expense, isNew }] = useExpenseFormContext()

    if (!isPerDiemExpense(expense)) {
        return <ReimbursementTypeSelector />
    }

    const [isDestinationFormValid, setIsDestinationFormValid] = useStateIfMounted(false)
    const [isDestinationFormChanged, setIsDestinationFormChanged] = useStateIfMounted(false)
    const [previousDestinations, setPreviousDestinations] = useStateIfMounted<FormPerDiemDestinationItinerary[]>(isNew ? [] : formatPerDiemDestinationDates(expense))
    const [errors, setErrors] = useStateIfMounted<string[]>([])

    const destinationMap = usePerDiemDestinationMap()

    const tripDailyExpenses: PerDiemDailyExpenses[] = expenseForm.getFieldValue(["dailyExpenses"]) ?? []
    const totalReimbursement = calculateTotalReimbursement(tripDailyExpenses, destinationMap)

    const expenditureRules = ExpenditureRules(!isDestinationFormChanged)
    const expenseRules = ExpenseRules(expense, employees)

    const buildDailyExpensesFormAndTable = () => {
        const currentFormState = expenseForm.getFieldsValue()
        const destinations: FormPerDiemDestinationItinerary[] = currentFormState?.destinations

        const firstDate = destinations[0]?.startDate
        const lastDate = destinations[destinations.length - 1]?.endDate
        const startMoment = moment(firstDate)
        const endMoment = moment(lastDate)

        const array: PerDiemDailyExpensesWithMomentDate[] = []
        let i = 0
        while (startMoment.isSameOrBefore(endMoment, "date")) {
            const isLastDay = startMoment.isSame(endMoment, "date")

            // We determine the location of the requester by where they are AT THE END OF THE DAY so we set this to the end of the day
            const date = moment(startMoment).endOf("day")
            let currentDestination: FormPerDiemDestinationItinerary
            if (!isLastDay) {
                // Search destinations that the user is on at the end of the day
                currentDestination = destinations.find((d) => date.isBetween(d.startDate, d.endDate, "minute"))!
            } else {
                // When it's the last day, just pick the last destination.
                currentDestination = destinations[destinations.length - 1]
            }
            const perDiemRate = destinationMap.get(currentDestination.destination!)!
            let expenditure = expenseForm.getFieldValue(["dailyExpenses", i, "expenditure"])
            if (!expenditure) expenditure = { breakfast: false, lunch: false, dinner: false, accommodation: false }
            array.push({ destination: perDiemRate._id, date: date, expenditure })
            startMoment.add(1, "day")
            i++
        }

        return array
    }

    const initializePerDiemDailyExpensesForm = (formValues: PerDiemDailyExpensesWithMomentDate[]) => {
        const dailyExpenses: PerDiemDailyExpensesWithMomentDate[] = expenseForm.getFieldValue(["dailyExpenses"])

        // If form content already exist, check before setting the state
        // Do not overwrite expenditures (but overwrite location) that has existed in the previous data of the same date.
        if (dailyExpenses) {
            for (const formValue of formValues) {
                const previousFormValue = dailyExpenses.find((e: PerDiemDailyExpensesWithMomentDate) => formValue.date.isSame(e.date, "day"))
                if (previousFormValue) {
                    formValue.expenditure = previousFormValue.expenditure
                }
            }
        }

        let updateObject = {}
        if (formValues?.length > 0) {
            updateObject = { performancePeriod: [formValues[0]?.date, formValues[formValues.length - 1]?.date] }
        }
        expenseForm.setFieldsValue({ dailyExpenses: formValues, ...updateObject })
        updateExpense({ dailyExpenses: formValues as any, ...updateObject })
    }

    const validateDestinationsForm = async (isSyncAction = false) => {
        const formDestinations: PerDiem["destinations"] = expense.destinations
        const collectedErrors: string[] = []
        if (!formDestinations || formDestinations.length === 0) {
            collectedErrors.push(t("validation:per_diem.destinations_invalid"))
            return collectedErrors
        }

        const firstDate = formDestinations[0]?.startDate
        const lastDate = formDestinations[formDestinations.length - 1]?.endDate

        // Validate each row one by one
        for (let i = 0; i < formDestinations.length; i++) {
            const relevantNamePaths: NamePath[] = [
                ["destinations", i, "endDate"],
                ["destinations", i, "destination"],
            ]
            if (i === 0) relevantNamePaths.push(["destinations", i, "startDate"])

            // Only perform validation here when the user is clicking on the sync button so errors will then popup
            if (isSyncAction) {
                try {
                    // Only perform validation in the destinations field.
                    await expenseForm.validateFields(relevantNamePaths)
                } catch (err) {
                    for (const errorField of err.errorFields) {
                        collectedErrors.push(...errorField.errors)
                    }
                }
            }
        }

        const startMoment = moment(firstDate)
        const endMoment = moment(lastDate)

        if (endMoment.isBefore(startMoment, "date")) {
            collectedErrors.push(t("validation:per_diem.end_time_less_than_start_time"))
        }

        if (Math.abs(startMoment.diff(endMoment, "day")) > PER_DIEM_DURATION_LIMIT) {
            collectedErrors.push(t("validation:per_diem.3_months_limit"))
        }

        return collectedErrors
    }

    const syncPerDiemDailyExpenseFormWithDestinationsForm = async () => {
        const errors = await validateDestinationsForm(true)
        if (errors.length > 0) {
            setErrors(errors)
            return
        }

        const dailyExpenses = buildDailyExpensesFormAndTable()
        initializePerDiemDailyExpensesForm(dailyExpenses)

        // When synced, we save the state of the destinations fields so can be compared in the future to check whether this fields has changed.
        setPreviousDestinations(expenseForm.getFieldValue(["destinations"]))

        setIsDestinationFormChanged(false)
    }

    // Use effect to check if the dates configuration are valid or not. Then determine the dates and the locations the user undergo through according to the forms
    useEffect(() => {
        const currentDestinations: PerDiemDestinationItinerary[] = expense.destinations
        // TODO: any better way to do this?
        setIsDestinationFormChanged(JSON.stringify(currentDestinations) !== JSON.stringify(previousDestinations))

        validateDestinationsForm().then((errors) => {
            setErrors(errors)
            setIsDestinationFormValid(errors.length === 0)
        })
    }, [JSON.stringify(expense.destinations)])

    useEffect(() => {
        updateExpense({ totalTaxPrice: 0, totalNetPrice: totalReimbursement, totalGrossPrice: totalReimbursement })
    }, [totalReimbursement])

    const leftSide = (
        <Tabs activeKey="PER_DIEM_CALCULATION">
            <Tabs.TabPane key="PER_DIEM_CALCULATION">
                <PerDiemReimbursementsPerDayTable perDiemExpense={expenseForm.getFieldsValue(true) as PerDiem} />
            </Tabs.TabPane>
        </Tabs>
    )

    const rightSide = (
        <>
            <ReimbursementTypeSelector />
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <p>{t("info:per_diem.foreign_trip_prompt")}</p>
                </Col>
                <Col span={24}>
                    <PerDiemDestinationFormFieldsContainer />
                </Col>
                <Col span={22}>
                    <p>{t("info:per_diem.please_select")}</p>
                </Col>
                <Col span={2}>
                    {isDestinationFormChanged && (
                        <Button type="primary" disabled={!isDestinationFormValid} onClick={syncPerDiemDailyExpenseFormWithDestinationsForm}>
                            <ReloadOutlined />
                        </Button>
                    )}
                </Col>
                <Form.Item rules={expenditureRules.syncDestinations} className="w-full" name={["dailyExpenses"]}>
                    <PerDiemOptionTable />
                </Form.Item>
                <Col span={24}>
                    <div className="w-full flex justify-end gap-8">
                        <span>{t("info:per_diem.total_reimbursable_amount")} </span>
                        <span className="font-bold">
                            <PriceLabel currency={CurrencyEnum.EUR} value={totalReimbursement} interval={PriceIntervalEnum.ONE_TIME} />
                        </span>

                        <HoverableInfo message={t("tooltips:per_diem.total_calculated_amount")} type="info" />
                    </div>
                </Col>
            </Row>
            <Divider />
            <Row>
                <Col span={24}>
                    <Form.Item name={"description"} key="description" label={t("input:request.per_diem.reason")} rules={expenseRules.description}>
                        <Input.TextArea
                            className="leading-normal"
                            autoSize
                            rows={1}
                            name="description"
                            placeholder={t("placeholder:request.description")}
                            onChange={(e) => updateExpense({ description: e.target.value })}
                        />
                    </Form.Item>
                </Col>
                <Col span={24}>
                    <Form.Item label={t("label:performance_period")} name="performancePeriod" className="text-opacity-50">
                        <DatePicker.RangePicker
                            name="performancePeriod"
                            style={{ width: "100%" }}
                            format={moment.localeData().longDateFormat("l")}
                            placeholder={[t("placeholder:start_date"), t("placeholder:end_date")]}
                            getPopupContainer={getPopupAnchor()}
                            disabledDate={(date: moment.Moment) => {
                                return date.isAfter(moment(), "day")
                            }}
                        />
                    </Form.Item>
                </Col>
                <Col span={24} className="hidden">
                    <Form.Item key="totalNetPrice" name="totalNetPrice" hidden>
                        <Input name="totalNetPrice" />
                    </Form.Item>
                    <Form.Item key="totalGrossPrice" name="totalGrossPrice" hidden>
                        <Input name="totalGrossPrice" />
                    </Form.Item>
                    <Form.Item key="totalTaxPrice" name="totalTaxPrice" hidden>
                        <Input name="totalTaxPrice" />
                    </Form.Item>
                </Col>
            </Row>
            <ExpenseCommonInputFieldsForm />
        </>
    )

    return <ExpenseFormLayout leftSide={leftSide} rightSide={rightSide} />
}
